import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import OfflineBanner from './components/OfflineBanner';
import PageLayout from './components/PageLayout';

// Phase G.1 (2026-04-24)：WorkDirectionModal 已废弃——
// 老引导弹窗让用户手选方向的流程和 G.1 "问卷自动派生方向" 冲突。
// work_direction 现在统一由 /profile/setup 里的问卷生成；个人中心保留手改入口（通过 /api/users/job-category/ 一并改大类和方向）。

import HomePage from './pages/HomePage';
import LegacyLoginPage from './pages/LoginPage';
import UnifiedEntry from './components/UnifiedEntry';
import './auth/unified.css';
import LegacyRegisterPage from './pages/RegisterPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import AssessmentRulesPage from './pages/AssessmentRulesPage';
import AssessmentPage from './pages/AssessmentPage';
import ReportPage from './pages/ReportPage';
import ReportV5Page from './pages/ReportV5Page';
import ReportV5DetailedPage from './pages/ReportV5DetailedPage';
import PrintReportV5Page from './pages/PrintReportV5Page';
import PrintEnterpriseReportPage from './pages/PrintEnterpriseReportPage';
import PrintClassReportPage from './pages/PrintClassReportPage';
import HistoryPage from './pages/HistoryPage';
import ProfilePage from './pages/ProfilePage';
import LegacyPasswordResetPage from './pages/PasswordResetPage';
import EnterpriseRegisterPage from './pages/EnterpriseRegisterPage';

// 管理后台页面
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminQuestionsPage from './pages/admin/AdminQuestionsPage';
import AdminEnterprisesPage from './pages/admin/AdminEnterprisesPage';
import AdminAssessmentDataPage from './pages/admin/AdminAssessmentDataPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminAIStrategyPage from './pages/admin/AdminAIStrategyPage';
import AdminReportDetailPage from './pages/admin/AdminReportDetailPage';
import AdminRecycleBinPage from './pages/admin/AdminRecycleBinPage';
import AdminUserRecycleBinPage from './pages/admin/AdminUserRecycleBinPage';
import AdminEnterpriseStrategyPage from './pages/admin/AdminEnterpriseStrategyPage';
import AdminDimensionsPage from './pages/admin/AdminDimensionsPage';
import AdminEnterpriseFormConfigPage from './pages/admin/AdminEnterpriseFormConfigPage';
import AdminRiskTemplatesPage from './pages/admin/AdminRiskTemplatesPage';
import AdminDimIntroImagesPage from './pages/admin/AdminDimIntroImagesPage';
import AdminPromptsPage from './pages/admin/AdminPromptsPage';

import { canAccessDashboard, canAccessTrainingReco } from './auth/roles';
import PrivateRoute from './auth/PrivateRoute';

// V4 企业看板（M18 起接入真接口）
import ClassListPage from './pages/dashboard/ClassListPage';
import ClassOverviewPage from './pages/dashboard/ClassOverviewPage';
import ClassHeatmapPage from './pages/dashboard/ClassHeatmapPage';
import ClassRisksPage from './pages/dashboard/ClassRisksPage';
import ClassDistributionPage from './pages/dashboard/ClassDistributionPage';
import ClassRecommendationPage from './pages/dashboard/ClassRecommendationPage';
import DashboardEntryRedirect from './pages/dashboard/DashboardEntryRedirect';
import EnterpriseListPage from './pages/dashboard/EnterpriseListPage';

function LoginPage() { const { sso } = useAuth(); return (sso.enabled || sso.configurationError) ? <UnifiedEntry /> : <LegacyLoginPage />; }
function RegisterPage() { const { sso } = useAuth(); return (sso.enabled || sso.configurationError) ? <UnifiedEntry mode="register" /> : <LegacyRegisterPage />; }
function PasswordResetPage() { const { sso } = useAuth(); return (sso.enabled || sso.configurationError) ? <UnifiedEntry mode="reset" /> : <LegacyPasswordResetPage />; }

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>;
  return user ? <Navigate to="/" replace /> : children;
}

// V4 看板权限：instructor / enterprise_admin / admin / superadmin 任一
// （后端 IsInstructorOrAbove 是真实关卡；前端只是 UX 阻挡, 防学员误访 404 海洋）
function DashboardRoute({ children }) {
  const { user, loading, logout, sso } = useAuth();
  const navigate = useNavigate();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (canAccessDashboard(user)) return <PageLayout>{children}</PageLayout>;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3a6b' }}>无权访问企业看板</h2>
      <p style={{ color: '#64748b', fontSize: 14 }}>企业看板供讲师 / 企业管理员 / 超管使用。</p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={async () => { await logout(); if (!sso.enabled) navigate('/login'); }}
          style={{ background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          切换账号
        </button>
        <button onClick={() => navigate('/')}
          style={{ background: 'none', color: '#64748b', border: '1.5px solid #dde3ee', borderRadius: 8, padding: '9px 22px', fontSize: 14, cursor: 'pointer' }}>
          返回首页
        </button>
      </div>
    </div>
  );
}

// 培训建议门：enterprise_admin / admin / superadmin（讲师强制 403, 设计文档 4.3.3）
// 后端 @action(permission_classes=[IsEnterpriseAdminOrAbove]) 是真实关卡
function TrainingRecoRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (canAccessTrainingReco(user)) return children;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3a6b' }}>培训建议仅企业管理员可见</h2>
      <p style={{ color: '#64748b', fontSize: 14, maxWidth: 420 }}>
        培训建议涉及成本预算与人员安排，按设计规范仅企业管理员 / 超管可访问。讲师可继续使用班级总览、维度强弱、风险预警与差异分布。
      </p>
      <button onClick={() => navigate(-1)}
        style={{ background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        返回上一页
      </button>
    </div>
  );
}

function AdminRoute({ children }) {
  const { user, loading, logout, sso } = useAuth();
  const navigate = useNavigate();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = ['admin', 'superadmin', 'enterprise_admin'].includes(user.role);
  if (isAdmin) return children;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3a6b' }}>权限不足</h2>
      <p style={{ color: '#64748b', fontSize: 14 }}>当前账号（{user.display_name || user.email || '未绑定邮箱'}）无管理员权限，请切换账号后重试。</p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={async () => { await logout(); if (!sso.enabled) navigate('/login'); }}
          style={{ background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          切换账号
        </button>
        <button onClick={() => navigate('/')}
          style={{ background: 'none', color: '#64748b', border: '1.5px solid #dde3ee', borderRadius: 8, padding: '9px 22px', fontSize: 14, cursor: 'pointer' }}>
          返回首页
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineBanner />
    <AuthProvider>
      {/* ISSUE-005（2026-04-28）：开 v7 future flag,消除 console 持续刷的两条
          deprecation warning（v7_startTransition + v7_relativeSplatPath）。
          行为不变,只是提前 opt-in 7.x 即将默认开的两个特性。 */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* Phase G.1：不再全局渲染 WorkDirectionModal，方向由问卷派生 */}
        <Routes>
          {/* 公开页面 */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/enterprise/register" element={<PrivateRoute><EnterpriseRegisterPage /></PrivateRoute>} />

          {/* 普通用户 */}
          <Route path="/profile/setup" element={<PrivateRoute><ProfileSetupPage /></PrivateRoute>} />
          <Route path="/assessment/rules" element={<PrivateRoute><AssessmentRulesPage /></PrivateRoute>} />
          <Route path="/assessment/:id" element={<PrivateRoute><AssessmentPage /></PrivateRoute>} />
          <Route path="/report/:assessmentId" element={<PrivateRoute><ReportPage /></PrivateRoute>} />
          {/* V5 报告页（M5 同步）：preview 走本地 mock，其余走真后端（endpoint 见 api/reports.js） */}
          <Route path="/report/v5/preview" element={<ReportV5Page />} />
          <Route path="/report/v5/preview/detailed" element={<ReportV5DetailedPage />} />
          <Route path="/report/v5/:id" element={<PrivateRoute><ReportV5Page /></PrivateRoute>} />
          <Route path="/report/v5/:id/detailed" element={<PrivateRoute><ReportV5DetailedPage /></PrivateRoute>} />
          {/* Playwright PDF 印刷模式专用路由 —— 不嵌 PrivateRoute，鉴权由 print-data API 的 HMAC token 承担 */}
          <Route path="/print/v5/:id" element={<PrintReportV5Page />} />
          <Route path="/print/enterprise/:id" element={<PrintEnterpriseReportPage />} />
          <Route path="/print/class/:id" element={<PrintClassReportPage />} />
          <Route path="/history" element={<PrivateRoute><HistoryPage /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

          {/* 管理后台 */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/assessment-data" element={<AdminRoute><AdminAssessmentDataPage /></AdminRoute>} />
          <Route path="/admin/questions" element={<AdminRoute><AdminQuestionsPage /></AdminRoute>} />
          <Route path="/admin/enterprises" element={<AdminRoute><AdminEnterprisesPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin/ai-strategy" element={<AdminRoute><AdminAIStrategyPage /></AdminRoute>} />
          <Route path="/admin/reports/:assessmentId" element={<AdminRoute><AdminReportDetailPage /></AdminRoute>} />
          <Route path="/admin/recycle-bin" element={<AdminRoute><AdminRecycleBinPage /></AdminRoute>} />
          <Route path="/admin/users/recycle-bin" element={<AdminRoute><AdminUserRecycleBinPage /></AdminRoute>} />
          <Route path="/admin/enterprise-strategy" element={<AdminRoute><AdminEnterpriseStrategyPage /></AdminRoute>} />
          <Route path="/admin/dimensions" element={<AdminRoute><AdminDimensionsPage /></AdminRoute>} />
          <Route path="/admin/form-config" element={<AdminRoute><AdminEnterpriseFormConfigPage /></AdminRoute>} />
          {/* V4 M23 / F.4: 风险模板编辑（后端 IsAdminRole, enterprise_admin 进了会拿到 403, 由页内错误态承接） */}
          <Route path="/admin/risk-templates" element={<AdminRoute><AdminRiskTemplatesPage /></AdminRoute>} />
          {/* V4 M24 / F.2: 维度介绍图（后端 IsAdminRole, enterprise_admin 同上由页内承接） */}
          <Route path="/admin/dim-intro-images" element={<AdminRoute><AdminDimIntroImagesPage /></AdminRoute>} />
          <Route path="/admin/prompts" element={<AdminRoute><AdminPromptsPage /></AdminRoute>} />

          {/* V4 企业看板（M18: 总览 + 热力图 / M19: 风险 + 差异 / M20: 培训建议）
              recommendation 路由用 TrainingRecoRoute 双层门 (DashboardRoute → TrainingRecoRoute) ——
              讲师走到看板 OK, 但点 recommendation 必须额外通过 enterprise_admin 门 */}
          <Route path="/dashboard" element={<DashboardEntryRedirect />} />
          <Route path="/dashboard/classes" element={<DashboardRoute><ClassListPage /></DashboardRoute>} />
          <Route path="/dashboard/classes/:id" element={<DashboardRoute><ClassOverviewPage /></DashboardRoute>} />
          <Route path="/dashboard/classes/:id/heatmap" element={<DashboardRoute><ClassHeatmapPage /></DashboardRoute>} />
          <Route path="/dashboard/classes/:id/risks" element={<DashboardRoute><ClassRisksPage /></DashboardRoute>} />
          <Route path="/dashboard/classes/:id/distribution" element={<DashboardRoute><ClassDistributionPage /></DashboardRoute>} />
          <Route path="/dashboard/classes/:id/recommendation" element={<DashboardRoute><TrainingRecoRoute><ClassRecommendationPage /></TrainingRecoRoute></DashboardRoute>} />

          {/* 企业级全员看板（spec §4.5）。班级页组件复用，scope="enterprise" */}
          <Route path="/dashboard/enterprises" element={<DashboardRoute><EnterpriseListPage /></DashboardRoute>} />
          <Route path="/dashboard/enterprises/:id" element={<DashboardRoute><ClassOverviewPage scope="enterprise" /></DashboardRoute>} />
          <Route path="/dashboard/enterprises/:id/heatmap" element={<DashboardRoute><ClassHeatmapPage scope="enterprise" /></DashboardRoute>} />
          <Route path="/dashboard/enterprises/:id/risks" element={<DashboardRoute><ClassRisksPage scope="enterprise" /></DashboardRoute>} />
          <Route path="/dashboard/enterprises/:id/distribution" element={<DashboardRoute><ClassDistributionPage scope="enterprise" /></DashboardRoute>} />
          <Route path="/dashboard/enterprises/:id/recommendation" element={<DashboardRoute><TrainingRecoRoute><ClassRecommendationPage scope="enterprise" /></TrainingRecoRoute></DashboardRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
